/*
 * Compre Junto (Frequentemente comprados juntos) — Ariel Malhas
 * Busca produtos complementares (Search & Discovery / Recommendations API,
 * intent=complementary), monta um card com checkboxes + total somado e um
 * botão "Adicionar selecionados". O add-all dispara o mesmo evento de carrinho
 * do tema (shopify:cart:lines-update), então popup, drawer e badge atualizam.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function readJSON(el) {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

class BuyTogether {
  constructor(root) {
    this.root = root;
    this.mount = root.querySelector('[data-bt-mount]');
    this.main = readJSON(root.querySelector('[data-bt-main]'));
    this.sectionId = root.dataset.sectionId;
    this.productId = root.dataset.productId;
    this.limit = parseInt(root.dataset.limit, 10) || 3;
    this.recsUrl = root.dataset.recsUrl || '/recommendations/products';
    this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.labels = {
      heading: root.dataset.heading || 'Compre junto',
      add: root.dataset.addLabel || 'Adicionar selecionados',
      this: root.dataset.thisLabel || 'Este produto',
      total: root.dataset.totalLabel || 'Total dos selecionados:',
    };
    this.items = [];
  }

  async init() {
    if (!this.mount || !this.main) return this.remove();

    const recs = await this.fetchRecs();
    if (!recs || recs.length === 0) return this.remove();

    // Produto atual sempre incluído (fixo); complementares marcáveis.
    this.items = [
      {
        main: true,
        title: this.main.title,
        image: this.main.image,
        variantId: this.liveMainVariantId() || this.main.variantId,
        priceCents: this.liveMainPriceCents() != null ? this.liveMainPriceCents() : this.main.price,
        available: true,
        checked: true,
      },
    ].concat(
      recs
        .filter((r) => r.variantId && r.available)
        .map((r) => ({
          main: false,
          title: r.title,
          url: r.url,
          image: r.image,
          variantId: r.variantId,
          priceCents: r.price,
          available: true,
          checked: true,
        }))
    );

    // Se sobrou só o produto principal (nenhum complementar comprável), some.
    if (this.items.length < 2) return this.remove();

    this.render();

    // Ao trocar a variante na PDP, preço/disponibilidade do produto principal
    // mudam — recalcula o total depois que ariel-product.js atualiza o DOM.
    document.addEventListener('change', (e) => {
      if (e.target.closest && e.target.closest('[data-option-index]')) {
        setTimeout(() => this.updateTotal(), 0);
      }
    });
  }

  /* ---- Disponibilidade da variante principal (dados da própria PDP) ---- */
  mainVariantAvailable() {
    if (!this.variants) {
      this.variants = readJSON(document.querySelector('[data-apdp-variants]')) || [];
    }
    const id = this.liveMainVariantId();
    if (!id || this.variants.length === 0) return true;
    const v = this.variants.find((x) => Number(x.id) === Number(id));
    return v ? v.available !== false : true;
  }

  remove() {
    if (this.root && this.root.parentElement) this.root.remove();
  }

  /* ---- Preço/variante do produto principal (lê ao vivo da PDP) ---- */
  liveMainVariantId() {
    const input = document.querySelector('[data-variant-id-input]');
    return input && input.value ? Number(input.value) : null;
  }

  liveMainPriceCents() {
    // O preço formatado (ex.: "R$ 1.234,56") vira centavos removendo tudo que
    // não é dígito — válido para BRL (sempre 2 casas decimais).
    const el = document.querySelector('.apdp__price-current[data-price]');
    if (!el) return null;
    const digits = (el.textContent || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : null;
  }

  /* ---- Recommendations API (intent=complementary) ---- */
  async fetchRecs() {
    var url =
      this.recsUrl +
      '?section_id=' +
      encodeURIComponent(this.sectionId) +
      '&product_id=' +
      encodeURIComponent(this.productId) +
      '&intent=complementary&limit=' +
      encodeURIComponent(this.limit);

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      return readJSON(html.querySelector('[data-bt-recs]'));
    } catch (e) {
      return null;
    }
  }

  /* ---- Render ---- */
  render() {
    const card = document.createElement('section');
    card.className = 'abt__card';

    const h = document.createElement('h2');
    h.className = 'abt__title';
    h.textContent = this.labels.heading;
    card.appendChild(h);

    // Cadeia de imagens
    const chain = document.createElement('div');
    chain.className = 'abt__chain';
    this.items.forEach((it, i) => {
      if (i > 0) {
        const plus = document.createElement('span');
        plus.className = 'abt__chain-plus';
        plus.textContent = '+';
        chain.appendChild(plus);
      }
      const box = document.createElement('div');
      box.className = 'abt__chain-item';
      box.dataset.chain = String(i);
      if (it.image) {
        const img = document.createElement('img');
        img.src = it.image;
        img.alt = '';
        img.loading = 'lazy';
        box.appendChild(img);
      }
      chain.appendChild(box);
    });
    card.appendChild(chain);

    // Lista com checkboxes
    const list = document.createElement('ul');
    list.className = 'abt__list';
    this.items.forEach((it, i) => {
      const li = document.createElement('li');
      li.className = 'abt__row';

      const check = document.createElement('input');
      check.type = 'checkbox';
      check.className = 'abt__check';
      check.checked = it.checked;
      check.dataset.index = String(i);
      check.setAttribute('aria-label', 'Incluir ' + (it.title || 'produto'));
      if (it.main) check.disabled = true; // produto atual é sempre incluído
      li.appendChild(check);

      const thumb = document.createElement('div');
      thumb.className = 'abt__thumb';
      if (it.image) {
        const img = document.createElement('img');
        img.src = it.image;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      li.appendChild(thumb);

      const info = document.createElement('div');
      info.className = 'abt__info';
      const name = it.url ? document.createElement('a') : document.createElement('span');
      name.className = 'abt__name';
      if (it.url) name.href = it.url;
      if (it.main) {
        const tag = document.createElement('span');
        tag.className = 'abt__tag';
        tag.textContent = this.labels.this + ':';
        info.appendChild(tag);
      }
      name.appendChild(document.createTextNode(it.title || ''));
      info.appendChild(name);
      li.appendChild(info);

      const price = document.createElement('span');
      price.className = 'abt__price';
      price.textContent = BRL.format((it.priceCents || 0) / 100);
      li.appendChild(price);

      list.appendChild(li);
    });
    card.appendChild(list);

    // Rodapé: total + botão
    const footer = document.createElement('div');
    footer.className = 'abt__footer';

    const total = document.createElement('div');
    total.className = 'abt__total';
    total.appendChild(document.createTextNode(this.labels.total + ' '));
    const totalVal = document.createElement('strong');
    totalVal.dataset.btTotal = '';
    total.appendChild(totalVal);
    footer.appendChild(total);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'abt__add';
    addBtn.dataset.btAdd = '';
    addBtn.textContent = this.labels.add;
    footer.appendChild(addBtn);

    card.appendChild(footer);

    this.mount.textContent = '';
    this.mount.appendChild(card);
    this.mount.hidden = false;

    // Eventos
    list.addEventListener('change', (e) => {
      const cb = e.target.closest('.abt__check');
      if (!cb) return;
      const idx = Number(cb.dataset.index);
      this.items[idx].checked = cb.checked;
      const chainBox = chain.querySelector('[data-chain="' + idx + '"]');
      if (chainBox) chainBox.classList.toggle('is-off', !cb.checked);
      this.updateTotal();
    });
    addBtn.addEventListener('click', () => this.addAll(addBtn));

    this.updateTotal();
  }

  updateTotal() {
    // Reflete o preço/variante atual do produto principal (caso troque).
    const liveCents = this.liveMainPriceCents();
    if (liveCents != null) this.items[0].priceCents = liveCents;

    const cents = this.items.reduce((sum, it) => (it.checked ? sum + (it.priceCents || 0) : sum), 0);
    const el = this.mount.querySelector('[data-bt-total]');
    if (el) el.textContent = BRL.format(cents / 100);

    const addBtn = this.mount.querySelector('[data-bt-add]');
    const anyExtra = this.items.some((it) => !it.main && it.checked);
    const mainOk = this.mainVariantAvailable();
    if (addBtn) {
      addBtn.disabled = !anyExtra || !mainOk;
      addBtn.title = mainOk ? '' : 'Produto indisponível no momento';
    }
  }

  collectItems() {
    // Sincroniza a variante do produto principal ao clicar.
    const mainId = this.liveMainVariantId() || this.items[0].variantId;
    const out = [];
    this.items.forEach((it, i) => {
      if (!it.checked) return;
      const id = i === 0 ? mainId : it.variantId;
      if (id) out.push({ id: Number(id), quantity: 1 });
    });
    return out;
  }

  cartSectionIds() {
    return Array.prototype.slice
      .call(document.querySelectorAll('cart-items-component'))
      .map((c) => c.dataset.sectionId)
      .filter(Boolean);
  }

  async refreshCart() {
    const comp = document.querySelector('cart-items-component');
    if (comp) {
      await customElements.whenDefined('cart-items-component');
      if (typeof comp.fetchCartData === 'function') return comp.fetchCartData();
    }
    const res = await fetch(this.cartUrl + '.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  async addAll(btn) {
    const items = this.collectItems();
    if (items.length === 0) return;

    const itemCount = items.reduce((s, i) => s + i.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    // Abre o popup imediatamente (feedback instantâneo), como o form do tema.
    this.root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    btn.classList.add('is-loading');
    btn.disabled = true;

    const payload = { items: items, sections: this.cartSectionIds().join(',') };

    try {
      const res = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status) {
        this.root.dispatchEvent(
          new CartErrorEvent({ error: data.message || 'Erro ao adicionar', code: 'INVALID' })
        );
        deferred.reject(new Error(data.message || 'add failed'));
        return;
      }

      const cart = await this.refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'buy-together',
          itemCount: itemCount,
          didError: false,
          sections: data.sections,
        },
      });
    } catch (err) {
      this.root.dispatchEvent(
        new CartErrorEvent({ error: (err && err.message) || 'Erro de rede', code: 'SERVICE_UNAVAILABLE' })
      );
      deferred.reject(err);
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }
}

function boot(ctx) {
  const scope = ctx && ctx.querySelectorAll ? ctx : document;
  scope.querySelectorAll('[data-buy-together]').forEach((root) => {
    if (root.__buyTogether) return;
    root.__buyTogether = new BuyTogether(root);
    root.__buyTogether.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', (e) => boot(e.target));
